import React, { useState, useEffect, useRef } from 'react';
import BtnTaratic from '../../components/ui/BtnTaratic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPhone, faPaperPlane } from '@fortawesome/free-solid-svg-icons';

const PLACEHOLDERS = [
  'clinica@ejemplo.com',
  'contacto@dentalsalud.es',
  'info@esteticadental.com',
  'admin@clinicamedica.es'
];

const RequestAccessPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Typewriter placeholder state
  const [placeholderText, setPlaceholderText] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  // Auto focus input on page load
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Typewriter effect loop
  useEffect(() => {
    const currentFullText = PLACEHOLDERS[placeholderIndex];
    
    // Determine delay based on state
    let delay = 90;
    if (isDeleting) {
      delay = 40;
    } else if (placeholderText === currentFullText) {
      delay = 2000; // Pause at end of text
    }

    const timer = setTimeout(() => {
      if (!isDeleting) {
        if (placeholderText.length < currentFullText.length) {
          setPlaceholderText(currentFullText.slice(0, placeholderText.length + 1));
        } else {
          setIsDeleting(true);
        }
      } else {
        if (placeholderText.length > 0) {
          setPlaceholderText(currentFullText.slice(0, placeholderText.length - 1));
        } else {
          setIsDeleting(false);
          setPlaceholderIndex((prev) => (prev + 1) % PLACEHOLDERS.length);
        }
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [placeholderText, placeholderIndex, isDeleting]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubmitted(true);
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-4 py-12 bg-gradient-to-br from-blue-250/30 to-blue-450/20 text-gray-900 relative">
      {/* Background glow effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-[100px]" />
      </div>

      <div className="w-full max-w-md bg-white/80 backdrop-blur-xl border border-gray-100 rounded-[2rem] p-8 shadow-2xl drop-shadow-glow-white-xl relative z-10 ">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-3 tracking-tight">Solicitar Acceso</h1>
          <p className="text-gray-600">
            Déjanos tu correo o contáctanos directamente para empezar a usar nuestra IA en tu clínica.
          </p>
        </div>

        {submitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-50 border border-green-100 text-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner">
              <FontAwesomeIcon icon={faPaperPlane} className="text-2xl" />
            </div>
            <h3 className="text-xl font-bold mb-2">¡Solicitud Recibida!</h3>
            <p className="text-gray-600 mb-6">
              Nos pondremos en contacto contigo muy pronto a <span className="font-medium text-gray-900">{email}</span>.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline underline-offset-4"
            >
              Enviar otra solicitud
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Correo Electrónico de la Clínica</label>
              <div className="relative w-full">
                <input
                  ref={inputRef}
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-300 relative z-10"
                />
                {!email && (
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 font-normal flex items-center select-none z-20">
                    <span>{placeholderText}</span>
                    <span className="inline-block w-[2px] h-4 bg-blue-500 ml-0.5 animate-pulse" />
                  </div>
                )}
              </div>
            </div>
            <BtnTaratic color="blue" className="w-full py-3.5 shadow-lg shadow-blue-500/20">
              <span className="font-semibold text-lg">Solicitar Acceso</span>
            </BtnTaratic>
          </form>
        )}

        <div className="mt-8 pt-6 border-t border-gray-100">
          <p className="text-sm text-gray-500 text-center mb-4 font-medium">O contáctanos directamente:</p>
          <div className="flex flex-col gap-3">
            <a href="mailto:contacto@taratic.com" className="flex items-center justify-center gap-3 w-full py-3 rounded-xl border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all text-gray-700 font-medium text-sm group">
              <FontAwesomeIcon icon={faEnvelope} className="text-gray-400 group-hover:text-blue-500 transition-colors" />
              contacto@taratic.com
            </a>
            <a href="tel:+34684788269" className="flex items-center justify-center gap-3 w-full py-3 rounded-xl border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all text-gray-700 font-medium text-sm group">
              <FontAwesomeIcon icon={faPhone} className="text-gray-400 group-hover:text-blue-500 transition-colors" />
              (+34) 684 788 269
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestAccessPage;
